import React, { useState, useEffect, useMemo } from 'react';
import { EventSnapshot, TopEventItem } from '../../api/types';
import { PreMatchList } from './PreMatchList/PreMatchList';

interface TopEventsSportTableProps {
  sportId: string;
  sportName: string;
  topPreEvents: EventSnapshot[];
  hasSelectedEvent: boolean;
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string, isUp?: boolean, isDown?: boolean) => React.ReactNode;
  handleEventClick: (event: EventSnapshot) => void;
  formatTime: (timeStr: string) => string;
}

export const TopEventsSportTable: React.FC<TopEventsSportTableProps> = ({
  sportId,
  sportName,
  topPreEvents,
  hasSelectedEvent,
  renderMarketColHeader,
  renderMarketColCells,
  handleEventClick,
  formatTime
}) => {
  const [topEventsApiData, setTopEventsApiData] = useState<TopEventItem[]>([]);
  const [topEventsMarket1, setTopEventsMarket1] = useState<string | number>('');
  const [topEventsMarket2, setTopEventsMarket2] = useState<string | number>('');
  const [topEventsMarket3, setTopEventsMarket3] = useState<string | number>('');
  const [topEventsMarket4, setTopEventsMarket4] = useState<string | number>('');
  const [topEventsMarket5, setTopEventsMarket5] = useState<string | number>('');
  const [topEventsMarketOptions, setTopEventsMarketOptions] = useState<number[]>([]);
  const [topEventsFullData, setTopEventsFullData] = useState<Record<string, EventSnapshot>>({});
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      import('../../api/api'),
      import('../../utils/marketParsers')
    ]).then(([{ getTopEvents }, { getIdFromMarketKey }]) => {
      getTopEvents({ sport_id: sportId, limit: 8 }).then(res => {
        if (!isMounted) return;
        const tabs = res?.market_tabs || [];
        setTopEventsApiData(res?.items || []);
        
        const numericTabs = tabs.map(t => getIdFromMarketKey(sportId, t.market_key)).filter(Boolean) as number[];
        setTopEventsMarketOptions(numericTabs);
        
        if (sportId === 'sr:sport:1' || sportId === 'Soccer') {
          setTopEventsMarket1(getIdFromMarketKey(sportId, 'three_way') || numericTabs[0] || '');
          setTopEventsMarket2(getIdFromMarketKey(sportId, 'total') || numericTabs[1] || '');
          setTopEventsMarket3(getIdFromMarketKey(sportId, 'first_team_to_score') || numericTabs[2] || '');
          setTopEventsMarket4(getIdFromMarketKey(sportId, 'half_winner') || numericTabs[3] || '');
          setTopEventsMarket5(getIdFromMarketKey(sportId, 'btts') || numericTabs[4] || '');
        } else {
          setTopEventsMarket1(numericTabs[0] || '');
          setTopEventsMarket2(numericTabs[1] || '');
          setTopEventsMarket3(numericTabs[2] || '');
          setTopEventsMarket4(numericTabs[3] || '');
          setTopEventsMarket5(numericTabs[4] || '');
        }
      }).catch(() => {});
    });
    return () => { isMounted = false; };
  }, [sportId]);

  useEffect(() => {
    Promise.all([
      import('../../api/api'),
      import('../../utils/marketParsers')
    ]).then(([{ getTopEvents }, { getMarketKeyFromId }]) => {
      const keys = [
        getMarketKeyFromId(sportId, topEventsMarket1),
        getMarketKeyFromId(sportId, topEventsMarket2),
        getMarketKeyFromId(sportId, topEventsMarket3),
        getMarketKeyFromId(sportId, topEventsMarket4),
        getMarketKeyFromId(sportId, topEventsMarket5)
      ].filter(Boolean) as string[];

      if (keys.length === 0) return;

      Promise.all(
        Array.from(new Set(keys)).map(key => getTopEvents({ sport_id: sportId, market_key: key, limit: 8 }).catch(() => null))
      ).then(results => {
        const newFullData: Record<string, EventSnapshot> = {};
        
        results.forEach(res => {
          if (!res || !res.items) return;
          res.items.forEach(item => {
            if (!newFullData[item.event_id]) {
              newFullData[item.event_id] = {
                event_id: item.event_id,
                event: item.event,
                markets: [],
                scope: 'pre',
                version: 1,
                status: { home_score: 0, away_score: 0 }
              } as EventSnapshot;
            }
            if (item.primary_market) {
              if (!newFullData[item.event_id].markets.find(m => m.canonical_key === item.primary_market.canonical_key)) {
                newFullData[item.event_id].markets.push({
                  market_id: item.primary_market.market_id,
                  canonical_key: item.primary_market.canonical_key,
                  name: item.primary_market.market_key,
                  specifiers: item.primary_market.specifiers,
                  outcomes: item.primary_market.outcomes,
                  status: 1,
                  favourite: false
                } as any);
              }
            }
          });
        });
        setTopEventsFullData(newFullData);
      });
    });
  }, [sportId, topEventsMarket1, topEventsMarket2, topEventsMarket3, topEventsMarket4, topEventsMarket5]);

  const filteredTopEventsTableData = useMemo(() => {
    const dynamicEvents = Object.values(topEventsFullData).filter(e => e.event.sport_id === sportId);
    if (dynamicEvents.length > 0) {
      return dynamicEvents.slice(0, 8);
    }

    return topEventsApiData
      .filter(ev => ev.event.sport_id === sportId)
      .slice(0, 8)
      .map(event => {
        const fullEvent = topPreEvents.find(e => e.event_id === event.event_id);
        if (fullEvent) return fullEvent;

        const pm = event.primary_market;
        const mockMarket = pm ? {
          market_id: pm.market_id,
          canonical_key: pm.canonical_key,
          name: pm.market_key,
          specifiers: pm.specifiers,
          outcomes: pm.outcomes,
          status: 1,
          favourite: false
        } : undefined;

        return {
          event_id: event.event_id,
          event: event.event,
          markets: mockMarket ? [mockMarket] : [],
          scope: 'pre',
          version: 1,
          status: { home_score: 0, away_score: 0 }
        } as EventSnapshot;
      });
  }, [topEventsApiData, sportId, topPreEvents, topEventsFullData]);

  if (filteredTopEventsTableData.length === 0) return null;

  return (
    <div className="mb-4">
      <PreMatchList
        title={sportName}
        hideSecondMarket={hasSelectedEvent}
        events={filteredTopEventsTableData}
        activeSport={sportId}
        setActiveSport={() => {}}
        sportsTab={[]}
        topMarket1={topEventsMarket1}
        setTopMarket1={setTopEventsMarket1}
        topMarket2={topEventsMarket2}
        setTopMarket2={setTopEventsMarket2}
        topMarket3={topEventsMarket3}
        setTopMarket3={setTopEventsMarket3}
        topMarket4={topEventsMarket4}
        setTopMarket4={setTopEventsMarket4}
        topMarket5={topEventsMarket5}
        setTopMarket5={setTopEventsMarket5}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
        options={topEventsMarketOptions}
        renderMarketColHeader={renderMarketColHeader}
        renderMarketColCells={renderMarketColCells}
        handleEventClick={handleEventClick}
        formatTime={formatTime}
        showViewAll={false}
      />
    </div>
  );
};
